// Room Template editor: turns a template's legacy scope values (hard-coded
// dropdown text and per-room counts that match nothing in the catalog) into
// real Standard Elements. Every row is a PROPOSAL (utils/legacyScopeConversion):
// the admin picks the catalog row and ticks what to convert; nothing is chosen
// silently when several rows fit, and nothing saves until "Convert selected".
import { useMemo, useState } from "react";

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap" };
const td = { fontSize: 12, padding: "5px 8px", borderBottom: "1px solid var(--cs-border)", verticalAlign: "top" };
const sel = { fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 5, background: "var(--surface)", color: "var(--text)", maxWidth: 360 };

const patchText = (p) => !p ? "" : [
  p.IsRoomElement ? "flag as Room element" : null,
  p.ElementDiscipline ? "Architectural" : null,
  p.ElementQuantityBasis ? `quantity basis → ${p.ElementQuantityBasis.replace("_", " ")}` : null,
].filter(Boolean).join(", ");

export default function LegacyScopeConverter({ templateName, plan, busy, onApply }) {
  const [picks, setPicks] = useState(() => Object.fromEntries(plan.map(r => [r.field, r.preselected || ""])));
  const [checked, setChecked] = useState(() => Object.fromEntries(plan.map(r => [r.field, !!r.preselected])));
  const rows = useMemo(() => plan, [plan]);
  const chosen = rows.filter(r => !r.skip && checked[r.field] && picks[r.field]);

  return (
    <div style={{ border: "1px solid #93c5fd", background: "var(--tint-blue)", borderRadius: 8, padding: 10, margin: "6px 0" }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Convert {templateName}'s legacy scope values to Standard Elements</div>
      <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 8 }}>
        These values were typed against option text, not the catalog, so they never priced. Pick the real Item / Assembly for each, tick what to convert, then confirm.
        A converted dropdown value is cleared from the template; door / vision-panel / pass-through counts are left in place (other engines still read them).
        Where a catalog row needs flagging as a Room element or given a quantity basis, that edit is shown and made to the shared catalog row too.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={th}></th><th style={th}>Legacy value</th><th style={th}>Becomes</th><th style={th}>Catalog row</th><th style={th}>Also changes on the catalog row</th></tr></thead>
        <tbody>
          {rows.map(r => {
            const cand = r.candidates?.find(c => c.key === picks[r.field]);
            return (
              <tr key={r.field}>
                <td style={td}><input type="checkbox" disabled={!!r.skip || !picks[r.field]} checked={!!checked[r.field] && !r.skip && !!picks[r.field]} onChange={e => setChecked(c => ({ ...c, [r.field]: e.target.checked }))} /></td>
                <td style={td}><strong>{r.label}</strong><br /><span style={{ color: "var(--text3)" }}>{r.legacyValue}</span></td>
                <td style={td}>{r.skip ? <span style={{ color: "var(--text3)" }}>{r.skip}</span> : r.action === "wall" ? "This template's wall system" : r.isCount ? `Standard element × ${r.fixedQty} per room` : `Standard element, quantity from the room's ${r.basis.replace("_", " ")}`}</td>
                <td style={td}>
                  {r.skip ? "—" : !r.candidates.length ? <span style={{ color: "#b45309" }}>No catalog row fits — add the Item / Assembly first, then reopen.</span> : (
                    <select value={picks[r.field] || ""} onChange={e => { setPicks(p => ({ ...p, [r.field]: e.target.value })); setChecked(c => ({ ...c, [r.field]: !!e.target.value })); }} style={sel}>
                      <option value="">— pick the catalog row —</option>
                      {r.candidates.map(c => <option key={c.key} value={c.key} disabled={c.alreadyElement}>{c.label}{c.alreadyElement ? " (already on this template)" : ""}</option>)}
                    </select>
                  )}
                </td>
                <td style={{ ...td, color: "var(--text3)" }}>{cand?.flagPatch ? patchText(cand.flagPatch) : cand ? "nothing" : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
        <button disabled={busy || !chosen.length}
          onClick={() => onApply(chosen.map(r => ({ row: r, cand: r.candidates.find(c => c.key === picks[r.field]) })))}
          style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{busy ? "Converting…" : `Convert selected (${chosen.length})`}</button>
        <span style={{ fontSize: 11, color: "var(--text3)" }}>Nothing is changed until you press this.</span>
      </div>
    </div>
  );
}
